import type { CartItem, MarketplaceCart, Maybe } from 'graphql/types/graphql'
import type { ProductBaseGtm } from '../../../types/productBaseGtm'
import { getVisitorId } from '../utils/getVisitorId'
import type { ProductBase } from '@/types/productBase'
import { getProductForGtm } from '~/utils/getProductForGtm'

const CartType = {
    MARKETPLACE: 'marketplace',
    HOME_AND_YOU: 'home&you',
    MIXED: 'mixed',
} as const

type CheckoutParams = {
    products: ProductBaseGtm[]
    step: number
    basketTotal: number
    currentCode: string
    cartType: (typeof CartType)[keyof typeof CartType] | undefined
}

type FireTagMarketing = {
    ecomm_prodid: number[]
    ecomm_pvalue: number[]
    ecomm_pname: string[]
    ecomm_pquantity: number[]
}

export function useGtmCart() {
    const { $bus } = useNuxtApp()

    function factoryProduct({ product, parent, quantity }: CartItem): ProductBaseGtm {
        return {
            ...getProductForGtm(
                { ...product, productPrice: { ...product?.priceRange?.minimumPrice } } as ProductBase,
                quantity,
            ),
            id: parent && parent.product.typeId === 'configurable' ? (parent.sku ? parent.sku as unknown as number : parent.product.sku as unknown as number) : (product.sku as number),
            brand: product.brandLabel as string,
            ...((parent && parent.product.typeId === 'configurable') && { variantId: product.sku as number }),
        }
    }

    function checkCartType(marketplace: Maybe<MarketplaceCart>[]) {
        let type: (typeof CartType)[keyof typeof CartType] | undefined

        marketplace.some((cart) => {
            if (!cart) return false
            if (cart.shop?.type !== type && type) {
                type = CartType.MIXED
                return true
            }
            type = cart.shop?.type === 'operator' ? CartType.HOME_AND_YOU : CartType.MARKETPLACE
        })
        return type
    }

    function addToCart(product: ProductBaseGtm, parent,currencyCode: string, currentStore: string) {
        useGtm()?.trackEvent({
            event: 'addToCart',
            ecommerce: {
                currencyCode,
                add: {
                    products: product.variantId ? [product] : (parent && parent?.product?.typeId !== 'grouped') ? [{...product, variantId: product.id, id: parent.sku ? parent.sku : parent.product.sku}] : [product],
                },
            },
            visitorId: getVisitorId(),
            currentStore,
        })
    }

    function addGroupedProductToCart(parentSku: string, items: [ProductBase], currencyCode: string, currentStore: string) {
        useGtm()?.trackEvent({
            event: 'addToCart',
            ecommerce: {
                currencyCode,
                add: {
                    products: items.map(item => {
                        return getProductForGtm(item)
                    })
                },
            },
            visitorId: getVisitorId(),
            currentStore,
        })

        $bus.$emit('cartProductGroupedAdded', parentSku)
    }

    function removeFromCart(product: ProductBaseGtm, currencyCode: string, currentStore: string) {
        useGtm()?.trackEvent({
            event: 'removeFromCart',
            ecommerce: {
                currencyCode,
                remove: {
                    products: [product],
                },
            },
            visitorId: getVisitorId(),
            currentStore,
        })
    }

    async function prepareFireRemarketingTag(products: ProductBaseGtm[], basketTotal: number) {
        const { isUserLoggedIn } = useUser()

        return {
            ecomm_pagetype: 'cart',
            returnCustomer: await isUserLoggedIn(),
            ecomm_totalvalue: basketTotal,
            ...products.reduce(
                (acc, product) => {
                    acc.ecomm_prodid.push(product.id)
                    acc.ecomm_pvalue.push(product.price)
                    acc.ecomm_pname.push(product.name)
                    acc.ecomm_pquantity.push(product.quantity ?? 0)

                    return acc
                },
                { ecomm_prodid: [], ecomm_pvalue: [], ecomm_pname: [], ecomm_pquantity: [] } as FireTagMarketing,
            ),
        }
    }

    async function checkout({ products, step, basketTotal, currentCode, cartType }: CheckoutParams) {
        useGtm()?.trackEvent({
            event: 'checkout',
            ecommerce: {
                checkout: {
                    actionField: {
                        action: 'checkout',
                        step,
                    },
                    basketTotal,
                    products,
                    cartType,
                },
                currencyCode: currentCode,
            },
        })

        useGtm()?.trackEvent({
            event: 'fireRemarketingTag',
            google_tag_params: await prepareFireRemarketingTag(products, basketTotal),
        })
    }

    return {
        factoryProduct,
        removeFromCart,
        checkCartType,
        addToCart,
        checkout,
        addGroupedProductToCart
    }
}
